/** @file A texel cut as a texture crop: the kept texels fill the unchanged quad (design 21.6 step 5). */
import type { PerCorner } from '@zilo/pose3d-render-core/contract';
import { TILE_EDGES, type TileEdge } from '@zilo/pose3d-render-core/cut/tileEdges';

/**
 * Removing `c` texels of the extent `T` at `edge` shrinks only the texture rectangle: the kept `T - c` texels must span
 * the quad. With the full tile still mapped (the CornersResult quad, the cut strip discarded by `cutDist`), the edge's
 * two corners move away from their partners by `T / (T - c)`. Nothing kept (`T - c <= 0`): the quad is returned as is.
 */
export function cutStretchOf(quad: PerCorner<[number, number]>, edge: TileEdge, extent: number, texels: number): PerCorner<[number, number]> {
  const kept = extent - texels;
  if (texels <= 0 || kept <= 1e-9) return quad;
  const k = extent / kept;
  const { corners, partners } = TILE_EDGES[edge];
  const out = quad.map((p): [number, number] => [p[0], p[1]]) as PerCorner<[number, number]>;
  corners.forEach((ci, j) => {
    const c = quad[ci] ?? [0, 0];
    const p = quad[partners[j] ?? 0] ?? [0, 0];
    out[ci] = [p[0] + (c[0] - p[0]) * k, p[1] + (c[1] - p[1]) * k];
  });
  return out;
}
